import { useState } from 'react'
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import AxiosRequest from '../../../utils/AxiosRequest'
import config      from '../../../configuration/configuration.js'

// Dependencies: yarn add html2canvas jspdf

export default function Beacon4ExportButton({ containerRef, title = 'Beacon4_PerformanceNetworkOverview' }) {
  const [anchor,  setAnchor]  = useState(null)
  const [loading, setLoading] = useState(false)

  const captureAll = async () => {
    const html2canvas = (await import('html2canvas')).default
    const el = containerRef?.current
    if (!el) return null
    return await html2canvas(el, { scale: 3, useCORS: true, backgroundColor: '#f0f2f5', logging: false })
  }

  const captureEachChart = async () => {
    const html2canvas = (await import('html2canvas')).default
    const el = containerRef?.current
    if (!el) return []

    // Use data-chart-title attribute for reliable selection
    const cards = Array.from(el.querySelectorAll('[data-chart-title]'))
    if (!cards.length) {
      // Fallback: find boxes with canvas
      const canvasBoxes = Array.from(el.querySelectorAll('.MuiBox-root'))
        .filter(node => node.querySelector('canvas') && node.querySelector('p'))
      const results = []
      for (const card of canvasBoxes) {
        const name   = card.querySelector('p')?.textContent?.trim() ?? 'Chart'
        const canvas = await html2canvas(card, { scale: 3, backgroundColor: '#ffffff', logging: false })
        results.push({ name, imageBase64: canvas.toDataURL('image/jpeg', 0.95) })
      }
      return results
    }

    const results = []
    for (const card of cards) {
      const name   = card.getAttribute('data-chart-title') ?? 'Chart'
      const canvas = await html2canvas(card, { scale: 3, backgroundColor: '#ffffff', logging: false })
      results.push({ name, imageBase64: canvas.toDataURL('image/jpeg', 0.95) })
    }
    return results
  }

  const exportPDF = async () => {
    setAnchor(null)
    setLoading(true)
    try {
      const { jsPDF } = await import('jspdf')
      const canvas    = await captureAll()
      if (!canvas) return
      const w = canvas.width / 2, h = canvas.height / 2
      const pdf = new jsPDF({ orientation: w > h ? 'landscape' : 'portrait', unit: 'px', format: [w, h] })
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h)
      pdf.save(`${title}.pdf`)
    } finally { setLoading(false) }
  }

  const exportPDFPerChart = async () => {
    setAnchor(null)
    setLoading(true)
    try {
      const { jsPDF } = await import('jspdf')
      const charts    = await captureEachChart()
      if (!charts.length) return

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' })
      const pw  = pdf.internal.pageSize.getWidth()
      const ph  = pdf.internal.pageSize.getHeight()

      charts.forEach((chart, i) => {
        if (i > 0) pdf.addPage()
        // Title
        pdf.setFontSize(12)
        pdf.setTextColor(70, 85, 104)
        pdf.text(chart.name, pw / 2, 24, { align: 'center' })
        // Image — fit to page with padding
        const imgProps = pdf.getImageProperties(chart.imageBase64)
        const ratio    = imgProps.width / imgProps.height
        const maxW     = pw - 40
        const maxH     = ph - 50
        const w        = Math.min(maxW, maxH * ratio)
        const h        = w / ratio
        const x        = (pw - w) / 2
        pdf.addImage(chart.imageBase64, 'JPEG', x, 34, w, h)
      })

      pdf.save(`${title}_charts.pdf`)
    } finally { setLoading(false) }
  }

  const exportPNG = async () => {
    setAnchor(null)
    setLoading(true)
    try {
      const canvas = await captureAll()
      if (!canvas) return
      const link    = document.createElement('a')
      link.download = `${title}.png`
      link.href     = canvas.toDataURL('image/png')
      link.click()
    } finally { setLoading(false) }
  }

  const exportPPT = async () => {
    setAnchor(null)
    setLoading(true)
    try {
      const charts = await captureEachChart()
      if (!charts.length) return

      const res = await AxiosRequest({
        method:       'POST',
        url:          `${config.API_URL}Beacon4Export/Pptx`,
        data:         { title, charts },
        responseType: 'blob',
      })

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      })
      const url  = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href     = url
      link.download = `${title}.pptx`
      link.click()
      URL.revokeObjectURL(url)
    } finally { setLoading(false) }
  }

  return (
    <>
      <Button
        variant="outlined" size="small"
        onClick={e => setAnchor(e.currentTarget)}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={12} /> : <DownloadIcon sx={{ fontSize: 14 }} />}
        sx={{ height: 28, fontSize: 11, textTransform: 'none', px: 1.5 }}
      >
        {loading ? 'Exporting...' : 'Export'}
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,.12)' } }}>
        <MenuItem onClick={exportPDFPerChart} sx={{ fontSize: 12, gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#c53030', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PDF</Typography>
          </Box>
          Export all as PDF
        </MenuItem>
        <MenuItem onClick={exportPPT} sx={{ fontSize: 12, gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#d04423', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PPT</Typography>
          </Box>
          Export all as PowerPoint
        </MenuItem>
        <MenuItem onClick={exportPNG} sx={{ fontSize: 12, gap: 1 }}>
          <Box sx={{ width: 20, height: 20, bgcolor: '#3182ce', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PNG</Typography>
          </Box>
          Export all as Image
        </MenuItem>
      </Menu>
    </>
  )
}